import React, { useState, useEffect, useRef } from 'react';
import { foundationData } from '../data/mock';
import { BookOpen, TestTube, Coins, Trophy, ArrowRight, Quote, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

// Interactive card with hover tilt effect
const TiltCard = ({ children, className }) => {
  const [transform, setTransform] = useState('');
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;
    setTransform(`perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`);
  };

  const handleMouseLeave = () => {
    setTransform('perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)');
  };

  return (
    <div
      ref={cardRef}
      className={`transition-transform duration-200 ease-out ${className}`}
      style={{ transform }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};

const Foundation = ({ isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);
  const featureIcons = [
    <BookOpen key="book" className="w-6 h-6" />,
    <TestTube key="test" className="w-6 h-6" />,
    <Coins key="coins" className="w-6 h-6" />,
    <Trophy key="trophy" className="w-6 h-6" />,
  ];

  return (
    <section id="foundation" ref={sectionRef} className={`py-28 relative overflow-hidden ${isDarkMode ? 'bg-[#111A19]' : 'bg-[#E5D9B6]'}`}>
      {/* Animated background rings */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border rounded-full animate-scale-pulse opacity-50 ${isDarkMode ? 'border-[#284139]/20' : 'border-[#284139]/10'}`} />
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border rounded-full animate-scale-pulse opacity-50 ${isDarkMode ? 'border-[#284139]/20' : 'border-[#284139]/10'}`} style={{ animationDelay: '0.5s' }} />
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border rounded-full animate-scale-pulse opacity-50 ${isDarkMode ? 'border-[#E5D9B6]/10' : 'border-[#284139]/15'}`} style={{ animationDelay: '1s' }} />
      
      {/* Background decorations */}
      <div className={`absolute top-1/2 -translate-y-1/2 right-0 w-[700px] h-[700px] rounded-full blur-[120px] ${isDarkMode ? 'bg-[#284139]/15' : 'bg-[#284139]/10'}`} />
      <div className={`absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#E5D9B6]/5' : 'bg-[#284139]/5'}`} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className={`inline-flex items-center gap-2 text-sm font-semibold tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full transition-colors cursor-default ${
            isDarkMode 
              ? 'text-[#E5D9B6] bg-[#E5D9B6]/10 border border-[#E5D9B6]/20 hover:bg-[#E5D9B6]/20'
              : 'text-[#284139] bg-[#284139]/10 border border-[#284139]/20 hover:bg-[#284139]/20'
          }`}>
            <Sparkles size={14} className="animate-pulse" />
            Apply Now
          </span>
          <h2 className={`font-display text-4xl lg:text-5xl font-bold mb-3 leading-tight ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#284139]'}`}>
            PATHWAYS AFRICA<br />COHORT 26-27 APPLICATION
          </h2>
          
          <div className={`w-24 h-1.5 rounded-full mx-auto mt-6 ${isDarkMode ? 'bg-[#E5D9B6]' : 'bg-[#284139]'}`} />
        </div>

        {/* Main Content Card with premium glass effect */}
        <div className={`relative rounded-3xl p-10 lg:p-14 mb-12 overflow-hidden ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a2625] to-[#111A19] border border-[#284139]'
            : 'bg-gradient-to-br from-[#284139] to-[#1a2625] border border-[#284139]'
        }`}>
          {/* Decorative corner accent */}
          <div className={`absolute top-0 right-0 w-64 h-64 rounded-bl-full ${isDarkMode ? 'bg-gradient-to-bl from-[#E5D9B6]/10 to-transparent' : 'bg-gradient-to-bl from-[#E5D9B6]/20 to-transparent'}`} />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <p className={`text-lg leading-relaxed mb-8 ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/90'}`}>
              Pathways Africa is a transformative college counselling and leadership development program designed to support high-achieving African students in their pursuit of world-class higher undergraduate education, particularly at Ivy League and other top-tier universities in the United States on full-ride (100%) scholarships.
            </p>
            
            {/* Quote block */}
            <div className="relative pl-8">
              <Quote className={`absolute left-0 -top-2 w-8 h-8 ${isDarkMode ? 'text-[#E5D9B6]/30' : 'text-[#E5D9B6]/50'}`} />
              <p className={`text-base leading-relaxed italic ${isDarkMode ? 'text-[#809076]/80' : 'text-[#E5D9B6]/80'}`}>
                Rooted in a commitment to academic excellence, mentorship, and global citizenship, the program equips students with the tools, guidance, and exposure necessary to navigate the competitive college admissions process successfully.
              </p>
            </div>
          </div>
        </div>

        {/* Apply Button */}
        <div className="text-center mb-20">
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSdElR9Gt7aHl0IKSVAiMXkEdQkZ4Aijo2YrrTvZzm6QnSg8Lg/viewform"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              className={`group relative px-10 py-7 font-semibold text-lg rounded-2xl btn-transition inline-flex items-center gap-3 shadow-2xl hover:scale-105 overflow-hidden ${
                isDarkMode 
                  ? 'bg-[#E5D9B6] hover:bg-[#d4c9a5] text-[#111A19] shadow-[#E5D9B6]/30 hover:shadow-[#E5D9B6]/50'
                  : 'bg-[#284139] hover:bg-[#1a2625] text-[#E5D9B6] shadow-[#284139]/30 hover:shadow-[#284139]/50'
              }`}
            >
              <span className="relative z-10">Apply</span>
              <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform duration-200" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </Button>
          </a>
        </div>


        {/* Features Grid with hover animations */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {foundationData.features.map((feature, index) => (
            <TiltCard
              key={feature.title}
              className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`group relative p-7 rounded-2xl transition-all duration-500 h-full overflow-hidden ${
                isDarkMode 
                  ? 'bg-[#E5D9B6] border border-[#E5D9B6] hover:border-[#284139]'
                  : 'bg-[#284139] border border-[#284139] hover:border-[#E5D9B6]'
              }`}>
                {/* Hover gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br transition-all duration-500 ${
                  isDarkMode 
                    ? 'from-[#284139]/0 to-[#284139]/0 group-hover:from-[#284139]/5 group-hover:to-transparent'
                    : 'from-[#E5D9B6]/0 to-[#E5D9B6]/0 group-hover:from-[#E5D9B6]/5 group-hover:to-transparent'
                }`} />
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 ${
                    isDarkMode 
                      ? 'bg-[#284139] text-[#E5D9B6] group-hover:bg-[#111A19]'
                      : 'bg-[#E5D9B6] text-[#284139] group-hover:bg-[#d4c9a5]'
                  }`}>
                    {featureIcons[index]}
                  </div>
                  <h3 className={`font-semibold text-lg mb-3 ${isDarkMode ? 'text-[#284139]' : 'text-[#E5D9B6]'}`}>
                    {feature.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-[#284139]/80' : 'text-[#E5D9B6]/80'}`}>
                    {feature.description}
                  </p>
                </div>
              </div>
            </TiltCard>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Foundation;
